import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Container, Grid2 } from '@mui/material';
import {
  ApiHeader,
  ProjectCard,
  LoadingSpinner,
  ErrorDisplay,
  HomeLink,
} from '../components/common';
import { FeaturesTab, GuidesTab, SpecsTab } from '../components/ApiDetail';
import { fetchApiDefinitionById, clearCurrentApiDefinition } from '../store/apiDetails';
import {
  currentApiDefinitionSelector,
  apiDetailLoadingSelector,
  apiDetailErrorSelector,
  availableTabsSelector,
} from '../store/apiDetails/selectors';
import { getLocalizedContent } from '../utils/apiUtils';
import { Language, ApiDetailTab } from '../types/app';
import { AppDispatch, RootState } from '../store/store';
import { apiLoadingSelector } from '@/store/apiDefinitions/selectors';

const ApiDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const { i18n, t } = useTranslation();
  const [activeTab, setActiveTab] = useState(ApiDetailTab.FEATURES);

  const currentApiDefinition = useSelector((state: RootState) =>
    currentApiDefinitionSelector(state, slug),
  );
  const loading = useSelector(apiDetailLoadingSelector);
  const definitionLoading = useSelector(apiLoadingSelector);
  const error = useSelector(apiDetailErrorSelector);
  const availableTabs = useSelector((state: RootState) => availableTabsSelector(state, slug));
  const selectedLanguage = i18n.language as Language;

  const shouldFetch = useMemo(() => {
    return slug && !currentApiDefinition && !definitionLoading && !loading && !error;
  }, [slug, currentApiDefinition, definitionLoading, loading, error]);

  useEffect(() => {
    if (shouldFetch) {
      dispatch(fetchApiDefinitionById(slug!));
    }

    return () => {
      dispatch(clearCurrentApiDefinition());
    };
  }, [dispatch, slug, shouldFetch]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      if (tabParam === ApiDetailTab.GUIDES && availableTabs.includes(ApiDetailTab.GUIDES)) {
        setActiveTab(ApiDetailTab.GUIDES);
      } else if (tabParam === ApiDetailTab.SPECS && availableTabs.includes(ApiDetailTab.SPECS)) {
        setActiveTab(ApiDetailTab.SPECS);
      } else if (
        tabParam === ApiDetailTab.FEATURES &&
        availableTabs.includes(ApiDetailTab.FEATURES)
      ) {
        setActiveTab(ApiDetailTab.FEATURES);
      }
    }
  }, [searchParams, availableTabs]);

  useEffect(() => {
    if (!availableTabs.includes(activeTab) && availableTabs.length > 0) {
      setActiveTab(availableTabs[0]);
    }
  }, [availableTabs, activeTab]);

  const handleBack = () => {
    navigate('/');
  };

  const handleTabChange = (tab: string) => {
    if (availableTabs.includes(tab as ApiDetailTab)) {
      setActiveTab(tab as ApiDetailTab);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', tab);
      navigate(`/api-definitions/${slug}?${newSearchParams.toString()}`, { replace: true });
    }
  };

  if (loading) {
    return (
      <Container maxWidth='xl'>
        <LoadingSpinner height='50vh' />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth='xl'>
        <ErrorDisplay
          error={error}
          description={
            <>
              {t('apiDetail.loadError')} <HomeLink />.
            </>
          }
        />
      </Container>
    );
  }

  if (!currentApiDefinition) {
    return (
      <Container maxWidth='xl'>
        <ErrorDisplay
          error={t('apiDetail.notFound')}
          severity='warning'
          description={
            <>
              {t('apiDetail.notFoundMessage')} <HomeLink />.
            </>
          }
        />
      </Container>
    );
  }

  const localizedContent = getLocalizedContent(currentApiDefinition, selectedLanguage);

  const validActiveTab = availableTabs.includes(activeTab) ? activeTab : ApiDetailTab.FEATURES;

  const renderTabContent = () => {
    if (!currentApiDefinition) {
      return null;
    }
    switch (validActiveTab) {
      case ApiDetailTab.FEATURES:
        return <FeaturesTab apiDefinition={currentApiDefinition} />;
      case ApiDetailTab.GUIDES:
        return <GuidesTab />;
      case ApiDetailTab.SPECS:
        return currentApiDefinition.hasOpenApiSpec ? (
          <SpecsTab />
        ) : (
          <FeaturesTab apiDefinition={currentApiDefinition} />
        );
      default:
        return <FeaturesTab apiDefinition={currentApiDefinition} />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <ApiHeader
        apiHeaderData={{
          title: localizedContent?.title || t('apiDetail.fallbackTitle'),
          isPublic: currentApiDefinition?.isPublic || false,
          tags: localizedContent?.tags?.map((tag) => tag.name) || [],
        }}
        activeTab={validActiveTab}
        onTabChange={handleTabChange}
        onBackClick={handleBack}
        tabs={availableTabs}
      />

      <Grid2 container spacing={3} sx={{ mt: 3 }}>
        <Grid2 size={{ xs: 12, md: 8 }}>
          <Box>{renderTabContent()}</Box>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }}>
          <ProjectCard apiDefinition={currentApiDefinition} />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default ApiDetail;
